import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { BLOG_POSTS } from "@/data"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link } from "@tanstack/react-router"
import { motion } from "framer-motion"
import { ArrowRight } from "lucide-react"

export const Route = createFileRoute("/blog")({
  component: BlogPage,
})

const CATEGORY_COLORS: Record<string, string> = {
  Insights: "bg-blue-light text-blue",
  Compliance: "bg-amber-light text-amber-DEFAULT",
  Product: "bg-green-light text-green-DEFAULT",
}

function BlogPage() {
  useSEO({
    title: "Blog",
    description:
      "Insights on payment orchestration, compliance, fintech trends, and product updates from the Orchestrate team.",
    canonical: "https://orchestrate.global/blog",
  })

  return (
    <>
      {/* Hero */}
      <section className="bg-surface pt-32 pb-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <SectionLabel label="Blog" className="mb-5" />
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <h1 className="font-display mb-6 max-w-2xl text-5xl leading-tight tracking-tight sm:text-6xl lg:text-7xl">
              Insights from the <em className="text-blue not-italic">payments frontier.</em>
            </h1>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <p className="text-muted max-w-lg text-xl">
              The latest thinking on payment orchestration, compliance, and fintech.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Posts */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BLOG_POSTS.map((post, i) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                viewport={{ once: true, amount: 0.08 }}
                className="border-border hover:shadow-ink/8 group overflow-hidden rounded-2xl border bg-white transition-all duration-250 hover:-translate-y-1.5 hover:shadow-xl"
              >
                {/* Colored top bar */}
                <div className="from-blue to-blue-mid h-1 bg-gradient-to-r" aria-hidden="true" />
                <div className="p-6">
                  <div className="mb-4 flex items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${CATEGORY_COLORS[post.category] ?? "bg-surface text-muted"}`}
                    >
                      {post.category}
                    </span>
                    <span className="text-muted text-xs">{post.readTime}</span>
                  </div>
                  <h2 className="group-hover:text-blue mb-2 text-lg leading-snug font-semibold transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-muted mb-5 text-sm leading-relaxed">{post.excerpt}</p>
                  <div className="border-border flex items-center justify-between border-t pt-4">
                    <span className="text-muted text-xs">
                      {post.date} · {post.author}
                    </span>
                    <Link
                      to="/blog/$slug"
                      params={{ slug: post.slug }}
                      className="text-blue flex items-center gap-1 text-xs font-semibold transition-all duration-150 hover:gap-2"
                      aria-label={`Read ${post.title}`}
                    >
                      Read <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  )
}
